import { Typography } from '@mui/material';
import Loading from 'components/Loading';
import { useGetAlert } from 'lib/alert/hooks/useGetAlert';
import { useGetRecords } from 'lib/alert/hooks/useGetRecords';
import { styled } from '@mui/material/styles';
import { AlertStatus } from 'lib/alert/models';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MANTY_GREEN, MANTY_RED } from 'styles/theme';
import Flex from 'ui/Flex/Flex';
import FixedThresholdStatusText from './FixedThreshold/FixedThresholdStatusText';
import { getSortedAlertAndStateInfo } from './status_helpers';
import AlertBadge from './AlertBadge';
import GenericAlertEvolution from './GenericAlertEvolution';
import GenericAlertValue from './GenericAlertValue';

type Props = {
    alertId: number;
};

const StyAlertStatusBox = styled(Flex, { name: 'StyAlertStatusBox' })(
    ({ triggered }: { triggered: boolean }) => ({
        padding: '1rem',
        justifyContent: 'center',
        borderRadius: '10px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor: triggered ? MANTY_RED : MANTY_GREEN,
    }),
);

const StyDescription = styled(Typography, {
    name: 'StyDescription',
})({
    fontWeight: 300,
    color: '#666',
    lineHeight: '1.5em',
});

const StyChapterTitle = styled(Typography, {
    name: 'StyChapterTitle',
})({
    marginBottom: '0.5rem',
    marginTop: '1rem',
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'inline',
});

const AlertStatusModal = ({ alertId }: Props) => {
    const { t } = useTranslation();
    const { data: alert, isLoading: alertLoading } = useGetAlert(alertId);
    const { data: records, isLoading: recordsLoading } = useGetRecords(alertId);

    if (alertLoading || recordsLoading) {
        return <Loading />;
    }

    if (alert === undefined || records === undefined) {
        return <></>;
    }

    if (records.length === 0) {
        return <div>{t('alert.status.noRecords')}</div>;
    }

    const { records: recordsWithDate, since } =
        getSortedAlertAndStateInfo(records);
    const hasDimension = !!alert?.dimension;

    const CurrentStatus = (
        <div>
            <StyChapterTitle variant="h5">
                {t('alert.status.current')}
            </StyChapterTitle>
            <StyAlertStatusBox
                triggered={alert?.status === AlertStatus.TRIGGERED}
            >
                <AlertBadge status={alert.status} />
                <FixedThresholdStatusText
                    alert={alert}
                    recordValues={recordsWithDate[0].values}
                    since={since}
                    latestDate={recordsWithDate[0].date}
                />
            </StyAlertStatusBox>
        </div>
    );

    const CurrentValue = (
        <div>
            <StyChapterTitle variant="h5">
                {t('alert.status.value')}
            </StyChapterTitle>
            <GenericAlertValue
                alert={alert}
                records={recordsWithDate}
                hasDimension={hasDimension}
            />
        </div>
    );

    const History = (
        <div>
            <GenericAlertEvolution
                alert={alert}
                records={recordsWithDate}
                hasDimension={hasDimension}
            />
        </div>
    );

    return (
        <div>
            <StyTitle variant="h5">{alert.name}</StyTitle>
            <StyDescription>{t('alert.status.subtitle')}</StyDescription>
            {CurrentStatus}
            {CurrentValue}
            {History}
        </div>
    );
};

export default AlertStatusModal;
